import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GetMasterDataDeductionCategoriesDTO } from "@src/domain/dto/masterData";
import type { MasterDataDeductionCategory } from "@src/domain/entities/masterData";
import { GetMasterDataDeductionCategoriesUseCase } from "@src/application/useCases/masterData";
import { MasterDataDeductionCategoryRepository } from "@src/infrastructure/repositories/masterData";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  getMasterDataListCacheEntry,
  masterDataAllListCacheKey,
  setMasterDataListCacheEntry,
} from "./masterData/masterDataListCache";

interface MasterDataDeductionCategoriesDataContextValue {
  brandKey: string;
  deductionCategories: MasterDataDeductionCategory[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  refetch: () => void;
}

const MasterDataDeductionCategoriesDataContext =
  createContext<MasterDataDeductionCategoriesDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataDeductionCategories(): MasterDataDeductionCategoriesDataContextValue {
  const context = useContext(MasterDataDeductionCategoriesDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataDeductionCategories must be used within MasterDataDeductionCategoriesDataProvider",
    );
  }
  return context;
}

interface MasterDataDeductionCategoriesDataProviderProps {
  brandKey: string;
  children: ReactNode;
}

/**
 * Loads all matching deduction categories for the brand (lookup-style: items only).
 * Tenant DB is selected via X-Brand-Key on MasterDataGraphQlAdapter.
 */
export function MasterDataDeductionCategoriesDataProvider({
  brandKey,
  children,
}: Readonly<MasterDataDeductionCategoriesDataProviderProps>) {
  const getDeductionCategoriesUseCase = useMemo(
    () =>
      new GetMasterDataDeductionCategoriesUseCase(
        new MasterDataDeductionCategoryRepository(),
      ),
    [],
  );

  const initialCacheKey = masterDataAllListCacheKey(
    "deduction-categories",
    brandKey,
    "",
  );
  const initialCached =
    getMasterDataListCacheEntry<MasterDataDeductionCategory>(initialCacheKey);

  const [deductionCategories, setDeductionCategories] = useState<
    MasterDataDeductionCategory[]
  >(() => initialCached?.items ?? []);
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const lastReloadTokenRef = useRef(0);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    MasterDataGraphQlAdapter.getInstance().setBrandKey(brandKey);
    return () => {
      MasterDataGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = masterDataAllListCacheKey(
      "deduction-categories",
      brandKey,
      search,
    );
    const forceRefresh = reloadToken !== lastReloadTokenRef.current;
    const cached =
      getMasterDataListCacheEntry<MasterDataDeductionCategory>(cacheKey);

    if (cached && !forceRefresh) {
      setDeductionCategories(cached.items);
      setLoading(false);
      setError(null);
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await getDeductionCategoriesUseCase.execute(
          new GetMasterDataDeductionCategoriesDTO({
            includeInactive: true,
            search: search.trim() || null,
          }),
        );
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.items.length,
          page: 1,
          limit: result.items.length,
        });
        lastReloadTokenRef.current = reloadToken;
        setDeductionCategories(result.items);
      } catch (err) {
        if (cancelled) return;
        setDeductionCategories([]);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load master data deduction categories",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [brandKey, getDeductionCategoriesUseCase, reloadToken, search]);

  const value = useMemo(
    (): MasterDataDeductionCategoriesDataContextValue => ({
      brandKey,
      deductionCategories,
      loading,
      error,
      search,
      setSearch,
      refetch,
    }),
    [
      brandKey,
      deductionCategories,
      loading,
      error,
      search,
      setSearch,
      refetch,
    ],
  );

  return (
    <MasterDataDeductionCategoriesDataContext.Provider value={value}>
      {children}
    </MasterDataDeductionCategoriesDataContext.Provider>
  );
}
